import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  MessageCircle, Phone, Calendar, CheckCircle, 
  ArrowRight, Crown, ExternalLink, Clock, Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CoachPocketPage = () => {
  const { isVip } = useAuth();
  const navigate = useNavigate();

  // WhatsApp link with Heather's number
  const whatsappLink = "https://wa.me/16474441101";

  if (!isVip) {
    return (
      <div className="space-y-8" data-testid="coach-pocket-locked">
        <div className="card-cosmic p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-4">Coach in Your Pocket</h1>
          <p className="text-xl text-stone-600 max-w-xl mx-auto mb-8">
            You know you're meant for more! Get direct WhatsApp access to Heather whenever you need guidance.
          </p>
          <Button 
            onClick={() => navigate('/pricing')}
            className="btn-cosmic text-lg px-8 py-6"
            data-testid="upgrade-to-vip-btn"
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade to Coach in Your Pocket - $75/mo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="coach-pocket-page">
      {/* Header */}
      <div className="card-cosmic p-8 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-amber-500/5">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-stone-900">Coach in Your Pocket</h1>
            <p className="text-stone-600">I'm here when you need me... helping you get there</p>
          </div>
        </div>
        
        <p className="text-lg text-stone-700 mb-6">
          You're ready to step into your power and create the life you truly want on your terms. 
          I'm here to support you every step of the way.
        </p>

        <a 
          href={whatsappLink}
          className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-4 rounded-full font-medium transition-colors"
          data-testid="whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5 mr-2" />
          Message Heather on WhatsApp
          <ExternalLink className="w-4 h-4 ml-2" />
        </a>
      </div>

      {/* High-Proximity Support */}
      <div className="card-cosmic p-8 border-l-4 border-amber-500">
        <p className="text-xl font-semibold text-stone-800 mb-4">
          For women who don't want the shift to fade between calls.
        </p>
        <p className="text-lg text-stone-600 mb-6">
          This is high-proximity support designed to interrupt patterns in real time and reinforce ownership as you build it.
        </p>
        <div className="space-y-3 text-stone-700">
          <p>When something arises, you don't sit with it alone.</p>
          <p>You bring it forward.</p>
          <p>We clarify it quickly.</p>
          <p className="font-semibold text-stone-900">You move.</p>
        </div>
      </div>

      {/* What's Included */}
      <div className="card-cosmic p-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-6">What's Included</h2>
        
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <a 
            href="https://meetings-na3.hubspot.com/heather-stewart"
            target="_blank"
            rel="noopener noreferrer"
            className="p-6 bg-orange-50 rounded-2xl hover:bg-orange-100 transition-colors cursor-pointer group"
            data-testid="book-accountability-link"
          >
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-stone-900 mb-2 group-hover:text-orange-700 transition-colors">Ongoing Accountability</h3>
            <p className="text-stone-600 mb-3">
              Consistent check-ins to ensure you don't drift back into old patterns.
            </p>
            <span className="text-orange-600 font-medium text-sm flex items-center gap-1">
              Schedule a check-in <ExternalLink className="w-3 h-3" />
            </span>
          </a>

          <div className="p-6 bg-green-50 rounded-2xl">
            <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-stone-900 mb-2">Direct WhatsApp Access</h3>
            <p className="text-stone-600">
              Message me when you need perspective, clarity, or interruption.
            </p>
          </div>
        </div>

        {/* Closing statement */}
        <div className="text-center p-6 bg-stone-50 rounded-2xl">
          <p className="text-lg text-stone-700 font-medium mb-2">
            This is steady support — not silence between sessions.
          </p>
          <p className="text-stone-600">
            No spiraling alone. No waiting weeks to regroup.<br />
            <span className="font-semibold text-stone-800">Just grounded momentum.</span>
          </p>
        </div>
      </div>

      {/* Quick Access */}
      <div className="card-cosmic p-6 border-2 border-green-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-stone-900">Need support right now?</h3>
              <p className="text-stone-600 text-sm">Reach out on WhatsApp - I'm here for you</p>
            </div>
          </div>
          <a 
            href={whatsappLink}
            className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            data-testid="quick-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Open WhatsApp
          </a>
        </div>
      </div>

      {/* VIP Community Link */}
      <div className="card-cosmic p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-stone-900">VIP Member Conversations</h3>
              <p className="text-stone-600 text-sm">Connect with other VIP members</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/dashboard/vip-community')}
            variant="outline"
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
          >
            View Community
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CoachPocketPage;
